//@ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Paperclip, X } from "lucide-react";
import CustomMenuDropdown from "../common/CustomMenuDropdown";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../app/hooks";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { CrossIcon } from "../icons/Icons";
import { createAppScoreCardDataThunk, fetchScorecardByListDataFilter } from "../../features/ApplicationData/ApplicationDataSlice";
import { deleteAppScorecardDetails, fetchScoreCardPipelineDetailsData } from "../../features/scorecard/scoreCardSlice";
import { motion, AnimatePresence } from "framer-motion";
import { fetchAppSkillRatingByDetails } from "../../features/AppSkillRating/AppSkillRatingSlice";
import JobSkillScorecard from "./JobSkillScorecard";
import { getColorForLabel } from "../../utils/colorUtils";


// Function to determine badge color based on skill scale value
const getSkillScaleBadgeStyles = (scale) => {

  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10"
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};

// Function to get skill level label based on scale value
const getSkillLevelLabel = (scale) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Expert";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Proficient";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Competent";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "Limited";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Inadequate";

  return null;
};


// Function to determine rating color
const getRatingColor = (rating) => {
  if (!rating) return '#9ca3af'; // Gray for null ratings
  const score = parseFloat(rating);
  if (score >= 4) return '#10b981'; // Green for high ratings
  if (score >= 2.5) return '#f59e0b'; // Yellow/orange for medium
  return '#ef4444'; // Red for low ratings
};



const getRatingLabel = (rating) => {
  if (!rating) return null;

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) return "Strong Yes";
  if (ratingValue === 4) return "Yes";
  if (ratingValue === 3) return "Neutral";
  if (ratingValue === 2) return "No";
  if (ratingValue === 1) return "Strong No";

  return null;
};


// Function to get skill level label based on scale value
const getSkillLevelLabelCandidate = (scale) => {
  const scaleValue = parseFloat(scale);
  if (scaleValue >= 3.5 && scaleValue <= 4.0) return "Strong Yes";
  if (scaleValue >= 1.5 && scaleValue <= 3.4) return "Yes";
  if (scaleValue >= 0.5 && scaleValue <= 1.4) return "Neutral";
  if (scaleValue >= -1.5 && scaleValue <= 0.4) return "No";
  if (scaleValue >= -2.0 && scaleValue <= -1.6) return "Strong No";

  return null;
};


// Function to determine badge color based on skill scale value
const getSkillScaleBadgeStylesCandidate = (scale) => {

  // Parse the scale value to ensure it's a number
  const scaleValue = parseFloat(scale);

  // Set badge styles based on ranges
  if (scaleValue >= 3.5 && scaleValue <= 4.0) {
    // Expert: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (scaleValue >= 1.5 && scaleValue <= 3.4) {
    // Proficient: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (scaleValue >= 0.5 && scaleValue <= 1.4) {
    // Competent: Yellow
    return {
      bg: "bg-yellow-50",
      text: "text-yellow-700",
      ring: "ring-yellow-700/10"
    };
  } else if (scaleValue >= -1.5 && scaleValue <= 0.4) {
    // Limited: Orange
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  } else if (scaleValue >= -2.0 && scaleValue <= -1.6) {
    // Inadequate: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};



// Function to get badge styles based on rating value
const getRatingBadgeStyles = (rating) => {
  if (!rating) return {
    bg: "bg-gray-50",
    text: "text-gray-700",
    ring: "ring-gray-700/10"
  };

  const ratingValue = parseFloat(rating);

  if (ratingValue === 5) {
    // Strong Yes: Green
    return {
      bg: "bg-green-50",
      text: "text-green-700",
      ring: "ring-green-700/10"
    };
  } else if (ratingValue === 4) {
    // Yes: Light Green
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      ring: "ring-emerald-700/10"
    };
  } else if (ratingValue === 3) {
    // Neutral: Blue
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      ring: "ring-blue-700/10"
    };
  } else if (ratingValue === 2) {
    // No: Orange
    return {
      bg: "bg-orange-50",
      text: "text-orange-700",
      ring: "ring-orange-700/10"
    };
  } else if (ratingValue === 1) {
    // Strong No: Red
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      ring: "ring-red-700/10"
    };
  } else {
    // Default style for any other values
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      ring: "ring-gray-700/10"
    };
  }
};



const CircularRatingProgress = ({ rating, maxRating = 5, size = 36, strokeWidth = 5, color = '#3b82f6' }) => {
  // Handle null or undefined ratings and format to 1 decimal place
  const actualRating = rating ? parseFloat(rating).toFixed(1) : '0.0';

  // Calculate percentage
  const percentage = (parseFloat(actualRating) / maxRating) * 100;

  // Calculate circle properties
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Center position
  const center = size / 2;

  return (
    <div className="flex items-center justify-center bg-white rounded-lg">
      <div className="relative" style={{ width: size, height: size }}>
        {/* Background circle */}
        <svg width={size} height={size}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
          />
        </svg>

        {/* Score text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#00005506]" >
          <span className="text-[10px] leading-4 font-normal" style={{ color }}>
            {actualRating}
          </span>
        </div>
      </div>
    </div>
  );
};

const ratingOptions1 = [
  { value: 1, label: "Strong No" },
  { value: 2, label: "No" },
  { value: 3, label: "Neutral" },
  { value: 4, label: "Yes" },
  { value: 5, label: "Strong Yes" },
];


const RatingButton1 = ({ label, value, selectedValue, onClick }) => {
  const isSelected = selectedValue == value;

  return (
    <button
      className={`flex items-center gap-[6px] text-[13px] rounded-md px-2 py-1 text-xs font-medium capitalize transition-colors ${isSelected
        ? 'bg-indigo-600 text-white ring-1 ring-inset ring-indigo-600'
        : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-500/10'
        }`}
      onClick={() => onClick(value)}
      type="button"
    >
      {label}
    </button>
  );
};


const AutoResizeTextarea1Candidate = ({ value, onChange, placeholder, hasError }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      // Reset height to auto to get the correct scrollHeight
      textareaRef.current.style.height = "auto";
      // Set the height based on scrollHeight with a minimum of 60px
      textareaRef.current.style.height = `${Math.max(60, textareaRef.current.scrollHeight)}px`;
    }
  }, [value]);

  return (
    <div className="w-full">
      <textarea
        ref={textareaRef}
        className={`outline-none bg-transparent focus:outline-none focus:ring-0 text-[14px] text-dark-black-100 w-full m-0 placeholder:text-[#9e9ea0]
        border-1 border-solid ${hasError ? 'border-red-500' : 'border-light-gray-200'} rounded-md p-2 min-h-[60px] overflow-hidden resize-none leading-5`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        rows={1}
        maxLength={1000}
      />
      {hasError && <p className="text-red-500 text-xs mt-1">This field is required</p>}
    </div>
  );
};

function JobScorecardsSection({ job: jobProp, app: appProp, quickData }) {
  const [scorecard, setScorecard] = useState([]);
  const [loading, setLoading] = useState(true);
  // const { baseId, jobId, applicationId, skillId } = useParams<{ baseId: string; jobId: string; applicationId: string; skillId: string }>();
  const { baseId, jobId: jobIdFromParams, applicationId: applicationIdFromParams, skillId } = useParams<{
    baseId: string;
    jobId: string;
    applicationId: string;
    skillId: string
  }>();

  const jobId = jobProp || jobIdFromParams;
  const applicationId = appProp || applicationIdFromParams || quickData

  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const userDetailsData = localStorage.getItem('jobByHiringTeamMembers');
  const baseMembers = userDetailsData ? JSON.parse(userDetailsData) : [];
  const [pipelineStages, setPipelineStages] = useState([]);
  // const [openAccordionIds, setOpenAccordionIds] = useState({});
  const [openAccordionIds, setOpenAccordionIds] = useState(() => {
    // Create an object with all accordion IDs set to true
    const initialState = {};
    // If you have access to the scorecard data at this point:
    if (scorecard && scorecard.skill) {
      scorecard.skill.forEach(item => {
        initialState[item.id] = true;
      });
    }
    return initialState;
  });
  const navigate = useNavigate()
  const [showSkills, setShowSkills] = useState(false)
  const [openAccordionIds1, setOpenAccordionIds1] = useState({});
  const [appScorecardData, setAppScorecardData] = useState([])
  // const [loadingSkillRatings, setLoadingSkillRatings] = useState({});
  const [loadingAppScorecards, setLoadingAppScorecards] = useState({});
  const [openSkillAccordionIds, setOpenSkillAccordionIds] = useState({});
  const [openDeleteModalExp, setOpenDeleteModalExp] = useState(false)
  const [deleteLoadingExp, setDeleteLoadingExp] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [openAccordionIds1Candidate, setOpenAccordionIds1Candidate] = useState({});
  const [overallRating, setOverallRating] = useState(null);
  const [overallComment, setOverallComment] = useState("");
  const [appScorecardCandidateOpen, setAppScorecardCandidateOpen] = useState(false)
  const [overallRatingError, setOverallRatingError] = useState(false);
  const [savingData, setSavingData] = useState(false);
  const [openAccordionIds1CandidateCreate, setOpenAccordionIds1CandidateCreate] = useState({});
  const [editingScorecard, setEditingScorecard] = useState(null);
  const [openDeleteModalExpCandidate, setOpenDeleteModalExpCandidate] = useState(false)
  const [deleteLoadingExpCandidate, setDeleteLoadingExpCandidate] = useState(false)
  const [selectedIndexCandidate, setSelectedIndexCandidate] = useState(null);
  const [appScoreCardId, setAppScoreCardId] = useState(null);
  const [openSkillScorecards, setOpenSkillScorecards] = useState({});
  const [selectedSkillId, setSelectedSkillId] = useState(null);



  const getStageName = (stageId) => {
    // Get scorecard data from localStorage
    const scorecardData = JSON.parse(localStorage.getItem("jobPipelineStageData") || "{}");
    const stages = scorecardData?.stages || [];

    // Find the stage with matching ID
    const stage = stages.find(stage => stage.id === stageId);
    return stage ? stage.stage_name : null;
  };

  // Function to get user name by ID
  // Modified functions to handle AI case
  const getUserName = (userId) => {
    // Handle AI case
    if (userId === 0 || userId === "0") {
      return "AI";
    }
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.name : `User ${userId}`;
  };

  const getUserImage = (userId) => {
    // Return null for AI case
    if (userId === 0 || userId === "0") {
      return null;
    }
    const user = baseMembers.find(member => member.user_id === userId || member.user_id === Number(userId));
    return user ? user.profile_pic : null;
  };


  // Function to render rating progress bar
  const renderRatingProgressBar = (rating) => {
    if (!rating) return null;

    // Assuming rating is on a scale of 1-5
    const maxRating = 5;
    const percentage = (rating / maxRating) * 100;

    // Determine color based on rating
    let barColor = "#3b82f6"; // Default blue
    if (percentage >= 80) barColor = "#10b981"; // Green for high ratings
    else if (percentage >= 50) barColor = "#f59e0b"; // Yellow/orange for medium
    else barColor = "#ef4444"; // Red for low ratings

    return (
      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div
          className="h-2 rounded-full"
          style={{
            width: `${percentage}%`,
            backgroundColor: barColor
          }}
        ></div>
        <div className="text-xs text-gray-500 mt-1">{rating} out of {maxRating}</div>
      </div>
    );
  };

  useEffect(() => {
    if (baseId) {
      const fetchJob = async () => {
        // Try to get pipeline stages from localStorage
        const storedBase = localStorage.getItem("jobPipelineStageData");
        if (storedBase) {
          try {
            const parsedData = JSON.parse(storedBase);
            if (parsedData && parsedData.data && parsedData.data.stages) {
              setPipelineStages(parsedData.data.stages);
            }
          } catch (error) {
            console.error("Error parsing local storage data:", error);
          }
        } else {
          try {
            const res = await dispatch(
              fetchScoreCardPipelineDetailsData({
                baseId: Number(baseId),
                JobId: jobId
              })
            );
            if (res && res.data) {
              setPipelineStages(res?.data?.stages);
            }
          } catch (error) {
            console.error("Error fetching base member details:", error);
          }
        }
      };
      fetchJob();
    }
  }, [baseId, dispatch, jobId]);


  useEffect(() => {
    const fetchScorecard = async () => {
      if (baseId && applicationId) {
        setLoading(true);
        try {
          const res = await dispatch(
            fetchScorecardByListDataFilter({
              baseId: Number(baseId),
              appId: applicationId
            })
          ).unwrap();
          if (res.status === 200) {
            setScorecard(res.data);

            // Initialize openAccordionIds with all scorecard IDs set to true
            const initialOpenState = res.data.skill.reduce((acc, scorecard) => {
              acc[scorecard.id] = true;
              return acc;
            }, {});
            setOpenAccordionIds(initialOpenState);
          }
        } catch (error) {
          console.error("Error fetching scorecard:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchScorecard();
  }, [baseId, applicationId, dispatch]);


  // Function to handle accordion toggle
  const handleAccordionOpen = (id) => {
    setOpenAccordionIds(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Updated function to toggle only the specific skill scorecard
  const handleCreateSkillOpen = (id) => {
    setSelectedSkillId(id);
    //setOpenAccordionIds1 

    setOpenAccordionIds1(false)
    setOpenSkillScorecards(prev => ({
      ...prev,
      [id]: !prev[id] // Toggle the state for this specific ID
    }));
  };



  const handleEditClick = (appScorecard) => {
    navigate(`/${baseId}/job/${jobId}/app/${applicationId}/scorecards/${appScorecard.id}/edit`)
  }

  const handleDeleteClick = (appScoreCardId) => {
    setOpenDeleteModalExp(true)
    setSelectedIndex(appScoreCardId)
  }

  // Function to handle cancel button in delete modal
  const handleCloseModal = () => {
    setOpenDeleteModalExp(false);
  };

  const handleDeleteConfirm = async (appScorecard) => {

    setDeleteLoadingExp(true); // Start loading

    try {
      await dispatch(
        deleteAppScorecardDetails({
          baseId: Number(baseId),
          id: selectedIndex?.id,
        })
      ).unwrap();

      toast.success("App scorecard deleted successfully!");
      setOpenDeleteModalExp(false);
      // setSelectedFile(null)


      const res = await dispatch(
        fetchScorecardByListDataFilter({
          baseId: Number(baseId),
          appId: applicationId
        })
      ).unwrap();

      if (res.status === 200) {
        setScorecard(res.data);
      }
    } catch (error) {
      toast.error("Failed to delete app scorecard!");
    } finally {
      setDeleteLoadingExp(false); // Stop loading
    }
  };


  const handleAppSkillRating = (id, data) => {
    // Check if this accordion is already open
    const isCurrentlyOpen = openAccordionIds1[id] || false;

    // Close all accordions and only open the current one if it wasn't already open
    setOpenAccordionIds1((prev) => {
      // Start with an empty object (all accordions closed)
      const newState = {};

      // Only set the current accordion to open if it was previously closed
      if (!isCurrentlyOpen) {
        newState[id] = true;

        // Only fetch data if we're opening this accordion
        setLoadingAppScorecards(prev => ({
          ...prev,
          [id]: true
        }));

        if (baseId && applicationId && id) {
          dispatch(fetchAppSkillRatingByDetails({
            baseId: Number(baseId),
            appId: applicationId,
            appScoreId: id
          }))
            .unwrap()
            .then((response) => {
              if (response.status === 200) {
                setAppScorecardData(response.data.skillReting);
              }
            })
            .catch((error) => {
              console.error(`Error fetching skill rating details:`, error);
            })
            .finally(() => {
              setLoadingAppScorecards(prev => ({
                ...prev,
                [id]: false
              }));
            });
        }
      }

      return newState;
    });
  }


  const handleAppCandidateRating = (id, appScorecard, scorecardId) => {
    // Find the scorecard in the data
    const scorecardToEdit = appScorecard;

    // Set the values from the scorecard
    setOverallRating(scorecardToEdit.rating);
    setOverallComment(scorecardToEdit.comment || "");
    setEditingScorecard(scorecardToEdit);
    setAppScoreCardId(scorecardId)

    // Toggle the accordion
    setOpenAccordionIds1Candidate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1CandidateCreate(false)
  };



  const handleOverallRatingSelect = (value) => {
    setOverallRating(value);
  };

  // Handle overall comment change
  const handleOverallCommentChange = (e) => {
    setOverallComment(e.target.value);
    // if (overallCommentError) {
    //   setOverallCommentError(false);
    // }
  };

  // Update your handleEditClickCandidate function to also load the data
  const handleEditClickCandidate = (id) => {
    // Find the scorecard to edit
    const scorecardToEdit = scorecard.candidate
      .flatMap(card => card.appScorecards || [])
      .find(sc => sc.id === id);

    if (scorecardToEdit) {
      setOverallRating(scorecardToEdit.rating);
      setOverallComment(scorecardToEdit.comment || "");
      setEditingScorecard(scorecardToEdit);
    }

    setAppScorecardCandidateOpen(true);
    setOpenAccordionIds1Candidate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1CandidateCreate(false)
  }

  const handleCreateCandidate = (id, data) => {
    const scorecardToEdit = data;
    // Set the values from the scorecard
    setOverallRating("");
    setOverallComment("");
    setEditingScorecard("");
    setAppScoreCardId(id)

    setOpenAccordionIds1CandidateCreate((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setOpenAccordionIds1Candidate(false)
  }


  const handleSaveScorecardCandidateCreate = async (data) => {

    if (overallRating === null) {
      setOverallRatingError(true);
      return;
    }

    setOverallRatingError(false);

    setSavingData(true);
    try {
      const payload = {
        app_id: applicationId,
        scorecard_id: data.id || skillId,
        comment: overallComment,
        rating: overallRating

      };

      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload.status === 200) {
        toast.success("Candidate app scorecard saved successfully!");
        setOverallComment('')
        // Reload the data
        const res = await dispatch(
          fetchScorecardByListDataFilter({
            baseId: Number(baseId),
            appId: applicationId,
          })
        ).unwrap();
        if (res.status === 200) {
          setScorecard(res.data);
          setOpenAccordionIds1CandidateCreate(false)
        }

      } else {
        // Handle API error
        if (response?.error?.message) {
          toast.error('Rating should be selected' || `Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save candidate app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  // Finally, update your save function to handle both create and update
  const handleSaveScorecardCandidateUpdate = async () => {
    if (overallRating === null) {
      setOverallRatingError(true);
      return;
    }

    setOverallRatingError(false);


    setSavingData(true);
    try {
      const payload = {
        app_id: applicationId,
        scorecard_id: editingScorecard ? editingScorecard.scorecard_id || appScoreCardId : skillId,
        comment: overallComment,
        rating: overallRating
      };

      // If editing, add the scorecard id to the payload
      if (editingScorecard) {
        payload.id = editingScorecard.id;
      }

      const response = await dispatch(createAppScoreCardDataThunk({
        baseId: Number(baseId),
        data: payload
      }));

      if (response?.payload) {
        toast.success("Candidate app scorecard saved successfully!");
        setOverallComment('');
        setOverallRating(null);
        setEditingScorecard(null);

        // Close the accordion
        if (editingScorecard) {
          setOpenAccordionIds1Candidate((prev) => ({
            ...prev,
            [editingScorecard.id]: false,
          }));
        }

        // Reload the data
        const res = await dispatch(
          fetchScorecardByListDataFilter({
            baseId: Number(baseId),
            appId: applicationId
          })
        ).unwrap();
        if (res.status === 200) {
          setScorecard(res.data);
        }
      } else {
        if (response?.error?.message) {
          toast.error(`Error: ${response.error.message}`);
        } else {
          throw new Error("Failed to save candidate app scorecard");
        }
      }
    } catch (error) {
      console.error("Error saving scorecard:", error);
      toast.error("Failed to save app scorecard. Please try again.");
    } finally {
      setSavingData(false);
    }
  };


  const handleDeleteClickCandidate = (appScoreCardId) => {
    setOpenDeleteModalExpCandidate(true)
    setSelectedIndexCandidate(appScoreCardId)
  }

  // Function to handle cancel button in delete modal
  const handleCloseModalCandidate = () => {
    setOpenDeleteModalExpCandidate(false);
  };

  const handleDeleteConfirmCandidate = async (appScorecard) => {

    setDeleteLoadingExpCandidate(true); // Start loading

    try {
      await dispatch(
        deleteAppScorecardDetails({
          baseId: Number(baseId),
          id: selectedIndexCandidate?.id,
        })
      ).unwrap();

      toast.success("Candidate scorecard deleted successfully!");
      setOpenDeleteModalExpCandidate(false);


      const res = await dispatch(
        fetchScorecardByListDataFilter({
          baseId: Number(baseId),
          appId: applicationId
        })
      ).unwrap();

      if (res.status === 200) {
        setScorecard(res.data);
      }
    } catch (error) {
      toast.error("Failed to delete candidate scorecard!");
    } finally {
      setDeleteLoadingExpCandidate(false); // Stop loading
    }
  };








  return (
    <div>

      {/* candidate */}

      <span className="text-sm text-[#374151] font-medium leading-4 capitalize mb-3 block">
        Candidate
      </span>

      <div className="space-y-4">
        {loading ? (
          // <div className="flex items-center justify-center relative z-50" style={{ height: "100px" }}>
          //   <div className="simple-spinner-loader">
          //     <span></span>
          //   </div>
          // </div>
          ''

        ) : scorecard?.candidate?.length === 0 ? (
          // <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
          //   <svg
          //     xmlns="http://www.w3.org/2000/svg"
          //     width={24}
          //     height={24}
          //     viewBox="0 0 24 24"
          //     fill="none"
          //     stroke="currentColor"
          //     strokeWidth={2}
          //     strokeLinecap="round"
          //     strokeLinejoin="round"
          //     className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
          //   >
          //     <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          //     <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
          //     <path d="M12 9h.01" />
          //     <path d="M11 12h1v4h1" />
          //   </svg>
          //   <p className="text-[#0000007a] text-[13px]">No results found</p>
          // </div>
          ''

        ) : (
          [...scorecard.candidate].map((msg, index) => {
            const senderUser = baseMembers.find(member => member.user_id === msg.user_id) || {};
            const senderName = senderUser.name || `User ${msg.user_id}`;
            const senderImg = senderUser.profile_pic || null;

            const stageId = msg?.appScorecards?.find(item => item.stage_id)?.stage_id || msg.stage_id;
            const stageName = getStageName(stageId);

            const assignedToIds = msg.assigned_to ? msg.assigned_to.split(',') : [];
            const assignedUsers = assignedToIds.map(id => getUserName(id));

            const isOpen = openAccordionIds[msg.id] || false;

            const isSkillOpen = openSkillAccordionIds[msg.id] || false;
            const isAccordionOpenCandidateCreate = openAccordionIds1CandidateCreate[msg.id] || false;



            return (
              <>

                <div className="rounded-md cursor-pointer ring-1 ring-gray-200 feedback-delete-hover">
                  <div className="flex items-center bg-[#00005506]">
                    <div className="w-[80%] flex flex-col gap-1 px-4 pt-[15px] pb-[15px]">
                      <div className="flex justify-start items-center gap-x-2">
                        <span className="flex items-center gap-x-3">
                          <span className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                            style={{ backgroundColor: msg.scorecard_colour }}
                          >
                          </span>
                        </span>
                        <div className="flex gap-x-[6px] items-center">
                          <span className="text-[14px] text-[#374151] font-medium leading-4 capitalize">
                           {
                              msg.scorecard_name
                            }
                          </span>
                        </div>
                      </div>
                      <p className="font-normal text-[#374151] text-[13px] wg-antialiased leading-4">
                        {msg.scorecard_description}
                      </p>
                    </div>
                    <div className="w-[20%] pr-4 mr-0 flex items-center gap-x-2 justify-end">
                      {/* <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                        data-title='skills'>
                        <button

                          onClick={() => handleAccordionOpen(msg.id)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                          <motion.div
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={16}
                              height={16}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                          </motion.div>
                        </button>
                      </span> */}


                      <div className="flex shrink-0 pr-2">
                        <div
                          className="h-[36px] w-[36px] rounded-[8px] flex flex-row items-center justify-center text-white"

                        >
                          <CircularRatingProgress
                            rating={msg?.appScorecards[0]?.rating_average}
                            color={getRatingColor(msg?.appScorecards[0]?.rating_average)}
                          />
                        </div>
                      </div>


                      <span className="table-tooltip before:!bottom-[28px] before:!left-[38px]"
                        data-title='score candidate'>
                        <button

                          // onClick={() => handleCreateSkillOpen(msg.id)}
                          onClick={() => handleCreateCandidate(msg.id, msg)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">


                          Score
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-right w-[0.9rem] h-[0.9rem] text-[#808080]"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M5 12l14 0" />
                            <path d="M15 16l4 -4" />
                            <path d="M15 8l4 4" />
                          </svg>


                        </button>
                      </span>
                    </div>
                  </div>




                  <AnimatePresence>
                    {isAccordionOpenCandidateCreate && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                        <div className="flex items-center gap-2 my-3 flex-wrap px-4 pt-1">
                          {ratingOptions1.map((option) => (
                            <RatingButton1
                              key={option.value}
                              label={option.label}
                              value={option.value}
                              selectedValue={overallRating}
                              // onClick={(value) => handleOverallRatingSelect(skill.id, value)}
                              onClick={handleOverallRatingSelect}
                            />
                          ))}
                        </div>
                        {overallRatingError && (
                          <p className="text-red-500 text-xs mb-2 mt-[-6px]">Please select an overall rating</p>
                        )}

                        {/* Overall scorecard comment */}
                        <div className="px-4 pb-3">


                          <AutoResizeTextarea1Candidate
                            value={overallComment}
                            onChange={handleOverallCommentChange}
                            placeholder="Write an overall comment about this candidate..."
                          // hasError={overallCommentError}
                          />
                        </div>

                        <div className='mt-0 pb-4 flex justify-end px-4'>
                          <button
                            className="group inline-flex shrink-0 select-none justify-end leading-6
                             transition-colors duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 
                             disabled:pointer-events-none gap-0 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] rounded-md text-white bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                            type="button"
                            onClick={() => handleSaveScorecardCandidateCreate(msg)}
                          // disabled={savingData}
                          >
                            <div className='flex items-center gap-[6px]'>
                              Save
                              {savingData && (
                                <div className="simple-spinner">
                                  <span></span>
                                </div>
                              )}
                            </div>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>


                  {/* <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                      >
                        <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>
                        {msg.skills && msg.skills.length > 0 && (
                          <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">Skills</span>
                        )}
                        {msg.skills && msg.skills.length > 0 ? (
                          <div className="w-full p-4 space-y-2">
                            {msg.skills.map((skill, idx) => {

                              const styles = getSkillScaleBadgeStyles(skill.skill_value);

                              return (
                                <div className="flex flex-col gap-y-2" key={idx}>
                                  <div className="p-2 rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover 
                                  ">
                                    <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                                      <div className="flex items-center gap-x-[16px]">
                                        <div className=" shrink-0 w-[9px] h-[9px] rounded-full" style={{ background: skill.skill_colour }} />
                                        <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-5 capitalize">
                                          {skill.skill_name}
                                        </span>

                                        <div className=" cursor-default select-none flex flex-row" />
                                      </div>
                                      {
                                        skill.skill_value &&
                                        <span
                                          // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                          className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                        >
                                          {getSkillLevelLabel(skill.skill_value)}
                                        </span>
                                      }

                                    </div>
                                  </div>
                                </div>
                              )

                            })}
                          </div>
                        ) : <span className="text-[#0000007a] text-[13px] text-center block  py-4">Nothing here</span>}

                      </motion.div>
                    )}
                  </AnimatePresence> */}


                  {msg.appScorecards && msg.appScorecards.length > 0 && (
                    <>


                      <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>


                      <div className="w-full">
                        {msg.appScorecards.slice(0, 1).map((appScorecard, idx) => {
                          const styles = getSkillScaleBadgeStylesCandidate(appScorecard.rating_average);
                          return (
                            <div key={idx} className="px-4 py-2 flex items-center justify-between">
                              <div className="text-[14px] leading-4 text-gray-500">
                                {msg.appScorecards ? msg.appScorecards.filter(scorecard => scorecard.status === 'done').length : 0} ratings
                              </div>
                              {appScorecard.rating_average && (
                                <span
                                  className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                >
                                  {getSkillLevelLabelCandidate(appScorecard.rating_average)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>



                      <div className="w-full border-t border-[#e0e0e0] last-of-type:border-b-0">
                        {msg.appScorecards.map((appScorecard, idx) => {
                          const reviewerName = getUserName(appScorecard.assigned_to);
                          const reviewerImage = getUserImage(appScorecard.assigned_to)
                          const appStageName = appScorecard.stage_id ? getStageName(appScorecard.stage_id) : stageName;
                          const styles1 = getRatingBadgeStyles(appScorecard.rating);
                          const isAccordionOpenCandidate = openAccordionIds1Candidate[appScorecard.id] || false;
                          const styles = getSkillScaleBadgeStyles(appScorecard.rating_average);

                          const dropdownGroups = [
                            {
                              options: [
                                {
                                  label: `Edit`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleEditClickCandidate(appScorecard.id),
                                },
                                {
                                  label: `Delete`,
                                  className:
                                    "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                  onClick: () => handleDeleteClickCandidate(appScorecard),
                                },
                              ],
                            },
                          ];

                          return (
                            <div key={idx} className="flex items-center gap-x-2 border-b border-light-gray-200 last-of-type:border-b-0">


                              <div key={appScorecard.id || idx} className="w-full">
                                <ul className="list-none w-full  px-4">
                                  <li className="flex flex-row items-center justify-between text-[0.875rem] px-[0rem] py-4 w-full">

                                    <div className="flex-1 px-0 flex flex-col gap-y-[4px]">
                                      <div className="flex items-center gap-x-2 mt-[-2px] justify-between">
                                        <div className="flex items-center gap-x-2">
                                          <span className="flex items-center gap-x-3">
                                            {
                                              reviewerImage ? (
                                                <img
                                                  src={`${process.env.REACT_APP_API_BASE_URL}/${reviewerImage}`}
                                                  alt={`${reviewerName || 'Profile'}`}
                                                  className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                                                />
                                              ) : (
                                                <div style={{ backgroundColor: getColorForLabel(reviewerName.charAt(0) || '') }}
                                                  className="w-6 h-6 rounded-full flex items-center justify-center leading-5">
                                                  <span className="font-medium text-white leading-6"
                                                  >{reviewerName.charAt(0)}</span>
                                                </div>
                                              )
                                            }
                                          </span>
                                          <p className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                            {reviewerName}
                                          </p>
                                        </div>
                                        <div className="flex items-center gap-x-2">
                                          {
                                            appScorecard.rating &&
                                            <span
                                              // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                              className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                            >
                                              {getRatingLabel(appScorecard.rating)}
                                            </span>
                                          }
                                          <div className='ml-[-4px]'>
                                            <div className="[&>div]:shrink">
                                              <CustomMenuDropdown
                                                sections={dropdownGroups}
                                                icon={
                                                  <svg
                                                    stroke="currentColor"
                                                    fill="none"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                    height="1em"
                                                    width="1em"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <circle cx="12" cy="12" r="1"></circle>
                                                    <circle cx="12" cy="5" r="1"></circle>
                                                    <circle cx="12" cy="19" r="1"></circle>
                                                  </svg>
                                                }
                                                menuItemsClassName="left-auto"
                                              />



                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      {/* {
                                        appStageName &&
                                        <span className="inline-flex w-fit items-center rounded-md px-1 py-1 text-xs font-medium bg-gray-50 text-gray-600 ring-gray-500/10 capitalize ring-1 ring-inset ">
                                          {appStageName}
                                        </span>
                                      } */}

                                      {
                                        appScorecard.comment &&
                                        <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e] ml-[30px]">
                                          "{appScorecard.comment.trim()}"
                                        </p>
                                      }


                                    </div>
                                    <div className="flex gap-1 items-center">




                                      {/* {
                                        appScorecard.status === 'pending' &&
                                        <span
                                          onClick={() => handleCreateSkillOpen(msg.id)}
                                          className="inline-flex mt-[0px] w-fit items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize"
                                        >
                                          {appScorecard.status}
                                        </span>

                                      } */}
                                      {
                                        (appScorecard.status === 'pending') &&
                                        <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                                          data-title='skills'>
                                          <button
                                            onClick={() => handleAppCandidateRating(appScorecard.id, msg, appScorecard.scorecard_id)}
                                            className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                       outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                       [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                                            <motion.div
                                              animate={{ rotate: isAccordionOpenCandidate ? 180 : 0 }}

                                              transition={{ duration: 0.3 }}
                                            >
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width={16}
                                                height={16}
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth={2}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                                              >
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                <path d="M6 9l6 6l6 -6" />
                                              </svg>
                                            </motion.div>
                                          </button>

                                        </span>
                                      }

                                    </div>

                                  </li>

                                </ul>
                                <AnimatePresence>
                                  {isAccordionOpenCandidate && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      style={{ overflow: 'hidden' }}
                                    >
                                      <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                                      <div className="flex items-center gap-2 my-3 flex-wrap px-4 pt-1">
                                        {ratingOptions1.map((option) => (
                                          <RatingButton1
                                            key={option.value}
                                            label={option.label}
                                            value={option.value}
                                            selectedValue={overallRating}
                                            // onClick={(value) => handleOverallRatingSelect(skill.id, value)}
                                            onClick={handleOverallRatingSelect}
                                          />
                                        ))}
                                      </div>
                                      {overallRatingError && (
                                        <p className="text-red-500 text-xs mb-2 mt-[-6px]">Please select an overall rating</p>
                                      )}

                                      {/* Overall scorecard comment */}
                                      <div className="px-4 pb-3">

                                        <AutoResizeTextarea1Candidate
                                          value={overallComment}
                                          onChange={handleOverallCommentChange}
                                          placeholder="Write an overall comment about this candidate..."
                                        // hasError={overallCommentError}
                                        />
                                      </div>

                                      <div className='mt-0 pb-4 flex justify-end px-4'>
                                        <button
                                          className="group inline-flex shrink-0 select-none  justify-end text-sm leading-6 
                                          transition-colors duration-100 focus:outline-0 focus-visible:outline focus-visible:outline-2 
                                          focus-visible:outline-offset-2 disabled:pointer-events-none gap-0 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] rounded-md text-white
                                           bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                                          type="button"
                                          onClick={handleSaveScorecardCandidateUpdate}
                                        // disabled={savingData}
                                        >
                                          <div className='flex items-center gap-[6px]'>
                                            Save
                                            {savingData && (
                                              <div className="simple-spinner">
                                                <span></span>
                                              </div>
                                            )}
                                          </div>
                                        </button>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>

                              </div>

                            </div>

                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </>

            );
          })
        )}
      </div>


      {/* ---------------------skills------------------------ */}


      <div className="bg-[#e0e0e0] w-full h-[1px] mt-9 mb-[28px]"></div>
      <span className="text-sm text-[#374151] font-medium leading-4 capitalize mb-3 block">
        Skills
      </span>

      <div className="space-y-12">
        {loading ? (
          <div className="flex items-center justify-center relative z-50" style={{ height: "100px" }}>
            <div className="simple-spinner-loader">
              <span></span>
            </div>
          </div>
        ) : scorecard.skill.length === 0 ? (
          <div className="w-full flex h-[100px] justify-center items-center flex-col gap-y-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="icon icon-tabler icons-tabler-outline icon-tabler-info-triangle  w-[1.5rem] h-[1.5rem] text-[#808080]"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10.363 3.591l-8.106 13.534a1.914 1.914 0 0 0 1.636 2.871h16.214a1.914 1.914 0 0 0 1.636 -2.87l-8.106 -13.536a1.914 1.914 0 0 0 -3.274 0z" />
              <path d="M12 9h.01" />
              <path d="M11 12h1v4h1" />
            </svg>
            <p className="text-[#0000007a] text-[13px]">No results found</p>
          </div>

        ) : (
          [...scorecard.skill].map((msg, index) => {
            const senderUser = baseMembers.find(member => member.user_id === msg.user_id) || {};
            const senderName = senderUser.name || `User ${msg.user_id}`;
            const senderImg = senderUser.profile_pic || null;

            const stageId = msg?.appScorecards?.find(item => item.stage_id)?.stage_id || msg.stage_id;
            const stageName = getStageName(stageId);

            const assignedToIds = msg.assigned_to ? msg.assigned_to.split(',') : [];
            const assignedUsers = assignedToIds.map(id => getUserName(id));

            const isOpen = openAccordionIds[msg.id] || false;

            const isSkillOpen = openSkillAccordionIds[msg.id] || false;

            const isSkillScoreOpen = openSkillScorecards[msg.id] || false;


            return (
              <>
                <div className="rounded-md cursor-pointer ring-1 ring-gray-200 feedback-delete-hover">
                  <div className="flex items-center bg-[#00005506] border-b border-solid border-[#0009321f]">
                    <div className="w-[80%] flex flex-col gap-1 px-4 pt-[15px] pb-[15px]">
                      <div className="flex justify-start items-center gap-x-2">
                        <span className="flex items-center gap-x-3">
                          <span className="flex h-[8px] w-[8px] shrink-0 items-center justify-center rounded-[2px] border-none text-[0.625rem] font-medium text-white capitalize"
                            style={{ backgroundColor: msg.scorecard_colour }}
                          >

                          </span>
                        </span>
                        <div className="flex gap-x-[6px] items-center">
                          <span className="text-[14px] text-[#374151] font-medium leading-4 capitalize">
                            {
                              msg.scorecard_name
                            }
                          </span>

                        </div>
                      </div>
                      <p className="font-normal text-[#374151] text-[13px] wg-antialiased leading-4">
                        {msg.scorecard_description}
                      </p>
                    </div>
                    <div className="w-[20%] pr-4 mr-0 flex items-center gap-x-2 justify-end">
                      {/* <span className="table-tooltip before:!bottom-[28px] before:!left-[24px]"
                        data-title='skills'>
                        <button

                          onClick={() => handleAccordionOpen(msg.id)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                          outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                          [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">

<motion.div
                            // animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                         <div className="w-full flex items-center gap-x-2">
                         Skills
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={16}
                              height={16}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                            >
                              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                              <path d="M6 9l6 6l6 -6" />
                            </svg>
                         </div>
                          </motion.div>
                        </button>
                      </span> */}

                      {/* <div className="flex shrink-0 pr-2">
                        <div
                          className="h-[36px] w-[36px] rounded-[8px] flex flex-row items-center justify-center text-white"

                        >
                          <CircularRatingProgress
                            rating={msg?.appScorecards[0]?.rating_average}
                            color={getRatingColor(msg?.appScorecards[0]?.rating_average)}
                          />
                        </div>
                      </div> */}


                      <span className="table-tooltip before:!bottom-[28px] before:!left-[41px]"
                        data-title='Score skill'>
                        <button

                          onClick={() => handleCreateSkillOpen(msg.id)}
                          className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                          outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                          [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 text-[#808080] w-auto py-1 px-2 gap-x-2">

                          Score
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="icon icon-tabler icons-tabler-outline icon-tabler-arrow-narrow-right w-[0.9rem] h-[0.9rem] text-[#808080]"
                          >
                            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                            <path d="M5 12l14 0" />
                            <path d="M15 16l4 -4" />
                            <path d="M15 8l4 4" />
                          </svg>


                        </button>
                      </span>
                    </div>
                  </div>
                  <AnimatePresence>
                    {isSkillScoreOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                        className="mb-6"
                      >
                        <JobSkillScorecard
                          skillId={msg.id}
                          job={jobProp}
                          app={appProp}
                          quickData={quickData}
                          onSuccess={isSkillScoreOpen} // This will toggle the modal closed
                          onSuccess={(isOpen) => handleCreateSkillOpen(msg.id)} // This will toggle the modal closed


                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {
                    !isSkillScoreOpen &&
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ overflow: 'hidden' }}
                        >
                          {/* <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div> */}
                          {msg.skills && msg.skills.length > 0 && (
                            <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">Skills</span>
                          )}
                          {msg.skills && msg.skills.length > 0 ? (
                            <div className="w-full p-4 space-y-2">
                              {msg.skills.map((skill, idx) => {

                                const styles = getSkillScaleBadgeStyles(skill.skill_value);

                                return (
                                  <div className="flex flex-col gap-y-2" key={idx}>
                                    <div className="p-2 rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover 
                ">
                                      <div className="flex flex-row items-center justify-between flex-grow shrink-0">
                                        <div className="flex items-center gap-x-[16px]">
                                          <div className=" shrink-0 w-[9px] h-[9px] rounded-full" style={{ background: skill.skill_colour }} />
                                          <span className="flex-shrink whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-5 capitalize">
                                            {skill.skill_name}
                                          </span>

                                          <div className=" cursor-default select-none flex flex-row" />
                                        </div>
                                        {
                                          skill.skill_value &&
                                          <span
                                            // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                            className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                          >
                                            {getSkillLevelLabel(skill.skill_value)}
                                          </span>
                                        }

                                      </div>
                                    </div>
                                  </div>
                                )

                              })}
                            </div>
                          ) : <span className="text-[#0000007a] text-[13px] text-center block  py-4">Nothing here</span>}

                        </motion.div>
                      )}
                    </AnimatePresence>
                  }



                  {
                    !isSkillScoreOpen &&
                    <>
                      {msg.appScorecards && msg.appScorecards.length > 0 && (
                        <>
                          <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>

                          <div className="w-full">
                            {msg.appScorecards.slice(0, 1).map((appScorecard, idx) => {
                              const styles = getSkillScaleBadgeStylesCandidate(appScorecard.rating_average);
                              return (
                                <div key={idx} className="px-4 py-2 flex items-center justify-between">
                                  <div className="text-[14px] leading-4 text-gray-500">
                                    {msg.appScorecards ? msg.appScorecards.filter(scorecard => scorecard.status === 'done').length : 0} ratings

                                  </div>
                                  {appScorecard.rating_average && (
                                    <span
                                      className={`inline-flex items-center rounded-md ${styles.bg} px-2 py-1 text-xs font-normal ${styles.text} ring-1 ring-inset ${styles.ring}`}
                                    >
                                      {getSkillLevelLabelCandidate(appScorecard.rating_average)}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          <div className='bg-[#e0e0e0] w-full h-[1px] mb-0'></div>

                          <div className="w-full">
                            {msg.appScorecards.map((appScorecard, idx) => {
                              const reviewerName = getUserName(appScorecard.assigned_to);
                              const reviewerImage = getUserImage(appScorecard.assigned_to)
                              const appStageName = appScorecard.stage_id ? getStageName(appScorecard.stage_id) : stageName;
                              const styles1 = getRatingBadgeStyles(appScorecard.rating);
                              const isAccordionOpen = openAccordionIds1[appScorecard.id] || false;


                              const dropdownGroups = [
                                {
                                  options: [
                                    {
                                      label: `Edit`,
                                      className:
                                        "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                      onClick: () => handleEditClick(appScorecard),
                                    },
                                    {
                                      label: `Delete`,
                                      className:
                                        "py-[0.375rem] px-[0.75rem] bg-white text-[#000000eb] text-[13px] hover:bg-[#e7e7e8]",
                                      onClick: () => handleDeleteClick(appScorecard),

                                    },
                                  ],
                                },
                              ];

                              return (
                                <div key={idx} className="flex items-center gap-x-2 border-b border-light-gray-200 last-of-type:border-b-0">
                                  <div key={appScorecard.id || idx} className="w-full">
                                    <ul className="list-none w-full  px-4">
                                      <li className="flex flex-row items-center justify-between text-[0.875rem] px-[0rem] py-4 w-full">
                                        {/* <div className="flex shrink-0 pr-2">
                                    <div
                                      className="h-[40px] w-[40px] rounded-[8px] flex flex-row items-center justify-center text-white"

                                    >
                                      <CircularRatingProgress
                                        rating={appScorecard.rating}
                                        color={getRatingColor(appScorecard.rating)}
                                      />
                                    </div>
                                  </div> */}
                                        <div className="flex-1 px-0 flex flex-col gap-y-[4px]">
                                          <div className="flex items-center gap-x-2 mt-[-2px]">
                                            <span className="flex items-center gap-x-3">
                                              {
                                                reviewerImage ? (
                                                  <img
                                                    src={`${process.env.REACT_APP_API_BASE_URL}/${reviewerImage}`}
                                                    alt={`${reviewerName || 'Profile'}`}
                                                    className="h-6 w-6 flex-none rounded-full ring-1 ring-gray-900/10"
                                                  />
                                                ) : reviewerName === "AI" ? (
                                                  // Show AI icon
                                                  <div className="h-5 w-5 rounded-full bg-[#ffeab6] flex items-center justify-center text-xs text-gray-600">
                                                    <svg
                                                      xmlns="http://www.w3.org/2000/svg"
                                                      fill="none"
                                                      viewBox="0 0 24 24"
                                                      strokeWidth="1.5"
                                                      stroke="currentColor"
                                                      aria-hidden="true"
                                                      data-slot="icon"
                                                      className="w-[0.8rem] h-[0.8rem] text-yellow-700"
                                                    >
                                                      <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
                                                      />
                                                    </svg>

                                                  </div>
                                                ) : (
                                                  <div style={{ backgroundColor: getColorForLabel(reviewerName.charAt(0) || '') }}
                                                    className="w-6 h-6 rounded-full flex items-center justify-center leading-5 ring-1 ring-gray-900/10">
                                                    <span className="font-medium text-white leading-6"
                                                    >{reviewerName.charAt(0)}</span>
                                                  </div>
                                                )
                                              }
                                            </span>
                                            <p className="text-[0.8125rem] font-medium leading-4 text-[#1b1b1b] capitalize">
                                              {reviewerName}
                                            </p>

                                          </div>
                                          {/* <span className="inline-flex w-fit items-center rounded-md px-1 py-1 text-xs font-medium bg-gray-50 text-gray-600 ring-gray-500/10 capitalize ring-1 ring-inset ">
                                            {appStageName}
                                          </span> */}
                                          {
                                            appScorecard.comment &&
                                            <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                              "{appScorecard.comment}"
                                            </p>
                                          }


                                        </div>
                                        <div className="flex gap-1 items-center">

                                          {
                                            appScorecard.rating &&
                                            <span
                                              // className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-normal text-blue-700 ring-1 ring-inset ring-blue-700/10"
                                              className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                            >
                                              {getRatingLabel(appScorecard.rating)}
                                            </span>
                                          }



                                          {
                                            appScorecard.status === 'pending' &&
                                            <span
                                              onClick={() => handleCreateSkillOpen(msg.id)}
                                              className="inline-flex mt-[0px] w-fit items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-800 ring-1 ring-inset ring-yellow-600/20 capitalize"
                                            >
                                              {appScorecard.status}
                                            </span>

                                          }
                                          <span className="table-tooltip before:!bottom-[28px] before:!left-[8px]"
                                            data-title='skills'>
                                            <button
                                              onClick={() => handleAppSkillRating(appScorecard.id, msg)}
                                              className=" rounded-md relative inline-flex hover:bg-[#f4f4f5] items-center justify-center whitespace-nowrap text-sm font-medium transition-colors
                                     outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none 
                                     [&_svg]:shrink-0 border border-input bg-background shadow-sm shadow-black/5 hover:bg-accent hover:text-accent-foreground h-6 w-6">
                                              <motion.div
                                                animate={{ rotate: isAccordionOpen ? 180 : 0 }}

                                                transition={{ duration: 0.3 }}
                                              >
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  width={16}
                                                  height={16}
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth={2}
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  className="icon icon-tabler icons-tabler-outline icon-tabler-chevron-down w-[0.9rem] h-[0.9rem] text-[#808080]"
                                                >
                                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                  <path d="M6 9l6 6l6 -6" />
                                                </svg>

                                              </motion.div>
                                            </button>

                                          </span>
                                          <div className='ml-[-4px]'>
                                            <div className="[&>div]:shrink">
                                              <CustomMenuDropdown
                                                sections={dropdownGroups}
                                                icon={
                                                  <svg
                                                    stroke="currentColor"
                                                    fill="none"
                                                    strokeWidth="2"
                                                    viewBox="0 0 24 24"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    aria-hidden="true"
                                                    focusable="false"
                                                    height="1em"
                                                    width="1em"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <circle cx="12" cy="12" r="1"></circle>
                                                    <circle cx="12" cy="5" r="1"></circle>
                                                    <circle cx="12" cy="19" r="1"></circle>
                                                  </svg>
                                                }
                                                menuItemsClassName="left-auto"
                                              />



                                            </div>
                                          </div>
                                        </div>

                                      </li>

                                    </ul>
                                    <AnimatePresence>
                                      {isAccordionOpen && (
                                        <motion.div
                                          initial={{ height: 0, opacity: 0 }}
                                          animate={{ height: "auto", opacity: 1 }}
                                          exit={{ height: 0, opacity: 0 }}
                                          transition={{ duration: 0.3 }}
                                          style={{ overflow: 'hidden' }}
                                        >
                                          <div className='bg-[#e0e0e0] w-full h-[1px]'></div>
                                          {loadingAppScorecards[appScorecard?.id] ? (
                                            <div className="flex items-center justify-center py-4">
                                              <div className="simple-spinner-loader">
                                                <span></span>
                                              </div>
                                            </div>
                                          ) : (
                                            <>
                                              {appScorecardData && appScorecardData.length > 0 && (
                                                <span className="text-[12px] text-[#374151] font-medium leading-4 capitalize px-4 pt-3 block mb-[-2px]">
                                                  Skills
                                                </span>
                                              )}

                                              {appScorecardData && appScorecardData.length > 0 ? (
                                                <div className="w-full p-4 space-y-2">
                                                  {appScorecardData.map((skill, idx) => {
                                                    const styles1 = getRatingBadgeStyles(skill.skill_scale);

                                                    return (
                                                      <div className="flex flex-col gap-y-2" key={idx}>
                                                        <div className="p-4 flex items-start rounded-[0.375rem] bg-white border border-solid border-[#e3e3e3] relative pl-4 feedback-tags-hover">
                                                          <div className="flex flex-col items-start justify-between">
                                                            <div className="flex items-start gap-x-[8px] w-full">
                                                              <div
                                                                className="h-[8px] w-[8px] rounded-[2px] mt-[2px]"
                                                                style={{ background: skill.skill_colour }}
                                                              />
                                                              <div className="flex justify-between items-center w-full gap-y-2">
                                                                <span className="flex-shrink mt-[-12px] whitespace-nowrap overflow-hidden text-ellipsis font-normal text-[#1b1b1b] text-sm leading-4 capitalize">
                                                                  {skill.skill_name}
                                                                </span>
                                                                {
                                                                  skill.skill_scale &&
                                                                  <span
                                                                    className={`inline-flex items-center rounded-md ${styles1.bg} px-2 py-1 text-xs font-normal ${styles1.text} ring-1 ring-inset ${styles1.ring}`}
                                                                  >
                                                                    {getRatingLabel(skill.skill_scale)}
                                                                  </span>
                                                                }
                                                              </div>
                                                            </div>
                                                            {
                                                              skill.comment &&
                                                              <span className="text-[13px] mt-[6px] font-normal  leading-5 text-[#5c5c5e] ">
                                                                "{skill.comment}"
                                                              </span>
                                                            }
                                                          </div>

                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                </div>
                                              ) : (
                                                <span className="text-[#0000007a] text-[13px] text-center block py-4">
                                                  Nothing here
                                                </span>
                                              )}
                                            </>
                                          )}
                                        </motion.div>
                                      )}
                                    </AnimatePresence>

                                  </div>

                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  }
                </div>
              </>

            );
          })
        )}
      </div>


      {/* skill */}

      {openDeleteModalExp && (
        <Dialog open={true} onClose={handleCloseModal} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <CrossIcon />
                  </button>
                </div>
                <div className="flex flex-col gay-y-4">
                  <span className="leading-6 text-sm text-gray-500">
                    Do you want to sure delete this app scorecard?
                  </span>
                </div>
                <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="items-center px-[14px] py-1 text-[13px] font-medium h-[32px] inline-flex leading-6 w-fit justify-center rounded-md bg-white text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    type="button"
                    className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
                  >
                    {deleteLoadingExp ? (
                      <div className='flex items-center gap-[6px]'>
                        Delete
                        <div className="simple-spinner">
                          <span></span>
                        </div>
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}




      {/* candidate  */}
      {openDeleteModalExpCandidate && (
        <Dialog open={true} onClose={handleCloseModalCandidate} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
              >
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    onClick={handleCloseModalCandidate}
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Close</span>
                    <CrossIcon />
                  </button>
                </div>
                <div className="flex flex-col gay-y-4">
                  <span className="leading-6 text-sm text-gray-500">
                    Do you want to sure delete this candidate scorecard?
                  </span>
                </div>
                <div className="mt-5 sm:mt-4 flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={handleCloseModalCandidate}
                    className=" inline-flex leading-6 w-fit justify-center rounded-md bg-white items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirmCandidate}
                    type="button"
                    className="rounded-md bg-indigo-600 items-center px-[14px] py-1 text-[13px] font-medium h-[32px] text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 "
                  >
                    {deleteLoadingExpCandidate ? (
                      <div className='flex items-center gap-[6px]'>
                        Delete
                        <div className="simple-spinner">
                          <span></span>
                        </div>
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      )}

    </div>
  );
}

export default JobScorecardsSection;